import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Session from 'services/session';
import { getTenantId } from 'helpers/templated-experience.helper';
import Iframe from 'react-iframe';
import { LoadingIcon } from 'components/Shared/LoadingIcon/LoadingIcon';

import './DynamicContentManager.scss';
import { useQueryClient } from '@tanstack/react-query';

const MAIN_CLASS = 'dynamic-content-iframe-preview';
const CLASSES = {
  IFRAME_WRAPPER: `${MAIN_CLASS}__iframe-wrapper`,
  IFRAME: `${MAIN_CLASS}__iframe`,
  LOADING_WRAPPER: `${MAIN_CLASS}__loading-wrapper`,
};

export type DynamicContentManagerProps = {
  contentId?: string;
  moduleId?: string;
  openPinnedTab?: boolean;
  hideContentHeader?: boolean;
};

function DynamicContentManager({ contentId, moduleId, openPinnedTab, hideContentHeader }: DynamicContentManagerProps) {
  const tenantId = getTenantId();
  const queryClient = useQueryClient();
  const session = useMemo(() => new Session(tenantId), [tenantId]);

  // While testing locally with vue-content-manager
  // use http://localhost:3300 (by default)
  const contentManagerUrl = process.env.VUE_CONTENT_MANAGER_URL;
  const iframeUrl = `${contentManagerUrl}${
    contentId !== undefined
      ? `/content/edit/${contentId}`
      : `/content/manage${hideContentHeader ? '?hideHeader=true' : ''}`
  }`;
  const iframeTargetOrigin = useMemo(() => new URL(iframeUrl).origin, [iframeUrl]);

  const iframeWrapperRef = useRef<HTMLDivElement>(null);
  const [isIframeLoading, setIframeLoading] = useState(true);

  const iframeMessageListener = useCallback(
    async (event: MessageEvent) => {
      const { source, origin, data: { type, changes } = { type: '', changes: [] } } = event;

      const iframe = iframeWrapperRef.current?.querySelector('#dynamicContentManager') as HTMLIFrameElement;
      const iframeWindow = iframe?.contentWindow;
      const env = process.env.REACT_API_ENV;
      const host = process.env.REACT_APP_API_URL;
      const token = await session.getToken();

      if (changes && changes.find((change: any) => change.entity === 'zone')) {
        queryClient.invalidateQueries(['zoneconfigurations']);
      }

      if (
        !iframe ||
        !iframeWindow ||
        source !== iframeWindow ||
        origin !== iframeTargetOrigin ||
        type !== `${contentId !== undefined ? 'flb.contenteditor.ready' : 'flb.contentmanager.ready'}`
      ) {
        return;
      }

      if (!env || !host || !token) {
        return;
      }

      setIframeLoading(false);

      const contentInstanceId = contentId;
      const enableDesignAndCode = true;
      const openPinning = openPinnedTab;
      const messagePayload = {
        token,
        host,
        env,
        contentInstanceId,
        enableDesignAndCode,
        moduleId,
        openPinning,
      };

      iframeWindow.postMessage(messagePayload, iframeTargetOrigin);
    },
    [iframeTargetOrigin, session, contentId, moduleId, openPinnedTab, queryClient],
  );

  useEffect(() => {
    window.addEventListener('message', iframeMessageListener);

    return () => {
      window.removeEventListener('message', iframeMessageListener);
    };
  }, [iframeMessageListener]);

  return (
    <div className={MAIN_CLASS}>
      <div className={CLASSES.IFRAME_WRAPPER} ref={iframeWrapperRef}>
        {isIframeLoading && (
          <div className={CLASSES.LOADING_WRAPPER}>
            <LoadingIcon />
          </div>
        )}
        <Iframe
          className={CLASSES.IFRAME}
          title={`content - manager - ${tenantId} `}
          url={iframeUrl}
          width="100%"
          height="100%"
          id="dynamicContentManager"
          position="relative"
          sandbox={['allow-scripts', 'allow-same-origin', 'allow-downloads']}
        />
      </div>
    </div>
  );
}

export default DynamicContentManager;
